import React, { useEffect, useState, useContext } from 'react';
import { X, Loader2, ChevronDown, ArrowLeft, CheckCircle } from 'lucide-react';
import { authService } from '../../services/auth';
import { formatBrazilianPhoneNumber } from '../../utils/fieldCache';
import { ToastContext } from '../../App';
import { FEATURES } from '../../config/features';
const RegisterModal = ({
  isOpen,
  onClose,
  onLoginClick,
  pendingAnalysis = false
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    confirmed_email: '',
    password: '',
    passwordConfirmation: '',
    invite_code: '',
    phone: '',
    a_empresa_em_que_trabalho_e___: '',
    seu_cargo: '',
    numero_de_funcionarios_na_empresa_em_que_trabalho: '',
    area_de_atuacao: '',
    website: '',
    como_voce_ouviu_falar_sobre_a_conversion_: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [error, setError] = useState('');
  const [codeVerificationMessage, setCodeVerificationMessage] = useState('');
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const {
    addToast
  } = useContext(ToastContext);
  useEffect(() => {
    if (isOpen) {
      // Reset form state when modal is opened
      setStep(1);
      setError('');
      setIsSuccess(false);
      setCodeVerificationMessage('');
      setIsCodeVerified(false);
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        confirmed_email: '',
        password: '',
        passwordConfirmation: '',
        invite_code: '',
        phone: '',
        a_empresa_em_que_trabalho_e___: '',
        seu_cargo: '',
        numero_de_funcionarios_na_empresa_em_que_trabalho: '',
        area_de_atuacao: '',
        website: '',
        como_voce_ouviu_falar_sobre_a_conversion_: ''
      });
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    if (name === 'phone') {
      // Format phone number as user types
      setFormData(prev => ({
        ...prev,
        [name]: formatBrazilianPhoneNumber(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  const validateStep1 = () => {
    if (!formData.firstname.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.lastname.trim()) {
      setError('Sobrenome é obrigatório');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email é obrigatório');
      return false;
    }
    if (!formData.confirmed_email.trim()) {
      setError('Confirmação de email é obrigatória');
      return false;
    }
    if (formData.email !== formData.confirmed_email) {
      setError('Os emails não conferem');
      return false;
    }
    if (!formData.password.trim()) {
      setError('Senha é obrigatória');
      return false;
    }
    // Validação de senha completa
    if (formData.password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres');
      return false;
    }
    if (!/[A-Za-z]/.test(formData.password)) {
      setError('Senha deve conter pelo menos uma letra');
      return false;
    }
    if (!/[0-9]/.test(formData.password)) {
      setError('Senha deve conter pelo menos um número');
      return false;
    }
    if (!/[^A-Za-z0-9]/.test(formData.password)) {
      setError('Senha deve conter pelo menos um símbolo');
      return false;
    }
    if (!formData.passwordConfirmation.trim()) {
      setError('Confirmação de senha é obrigatória');
      return false;
    }
    if (formData.password !== formData.passwordConfirmation) {
      setError('As senhas não conferem');
      return false;
    }
    // Only validate invite code if feature is enabled
    if (FEATURES.REQUIRE_INVITE_CODE && !formData.invite_code.trim()) {
      setError('Código de convite é obrigatório');
      return false;
    }
    return true;
  };
  const validateStep2 = () => {
    if (!formData.phone.trim()) {
      setError('Telefone é obrigatório');
      return false;
    }
    if (!formData.a_empresa_em_que_trabalho_e___) {
      setError('Segmento é obrigatório');
      return false;
    }
    if (!formData.seu_cargo) {
      setError('Cargo é obrigatório');
      return false;
    }
    if (!formData.numero_de_funcionarios_na_empresa_em_que_trabalho) {
      setError('Número de funcionários é obrigatório');
      return false;
    }
    if (!formData.area_de_atuacao) {
      setError('Área de atuação é obrigatória');
      return false;
    }
    if (!formData.website.trim()) {
      setError('URL do site é obrigatória');
      return false;
    }
    return true;
  };
  const verifyInviteCode = async () => {
    if (!formData.email.trim()) {
      setError('Email é obrigatório para verificar o código');
      return false;
    }
    if (!formData.invite_code.trim()) {
      setError('Código de convite é obrigatório');
      return false;
    }
    setIsVerifyingCode(true);
    setError('');
    setCodeVerificationMessage('');
    try {
      const response = await authService.verifyInviteCode(formData.email, formData.invite_code);
      if (response.valid) {
        setIsCodeVerified(true);
        setCodeVerificationMessage('Código válido!');
        return true;
      } else {
        setIsCodeVerified(false);
        setError('Código inválido');
        return false;
      }
    } catch (error) {
      setIsCodeVerified(false);
      // Always show "Código inválido" for any error related to code verification
      setError('Código inválido');
      return false;
    } finally {
      setIsVerifyingCode(false);
    }
  };
  const handleNextStep = async () => {
    // Validar campos básicos primeiro
    if (!validateStep1()) {
      return;
    }
    // Only verify code if feature is enabled and code is not yet verified
    if (FEATURES.REQUIRE_INVITE_CODE && !isCodeVerified) {
      setIsVerifyingCode(true);
      const isValid = await verifyInviteCode();
      setIsVerifyingCode(false);
      if (!isValid) {
        return; // Não avança se o código for inválido
      }
    }
    // Se tudo estiver ok, avançar para o próximo passo
    setError('');
    setStep(2);
  };
  const handleVerifyCode = async e => {
    e.preventDefault();
    await verifyInviteCode();
  };
  const handlePrevStep = () => {
    setStep(1);
    setError('');
  };
  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateStep2()) {
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      // Prepare data for API
      const callbackUrl = `${window.location.origin}/verificar-email?token={{token}}`;
      // Format phone number - remove non-digits
      const phoneDigits = formData.phone.replace(/\D/g, '');
      // Ensure URL has protocol
      let website = formData.website.trim();
      if (website && !website.match(/^https?:\/\//)) {
        website = 'https://' + website;
      }
      // Map form fields to backend expected fields - keeping original values
      const dataToSubmit = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: phoneDigits.startsWith('+55') ? phoneDigits : `+55${phoneDigits}`,
        website: website,
        como_voce_ouviu_falar_sobre_a_conversion_: formData.como_voce_ouviu_falar_sobre_a_conversion_ || '',
        a_empresa_em_que_trabalho_e___: formData.a_empresa_em_que_trabalho_e___,
        seu_cargo: formData.seu_cargo,
        numero_de_funcionarios_na_empresa_em_que_trabalho: formData.numero_de_funcionarios_na_empresa_em_que_trabalho,
        area_de_atuacao: formData.area_de_atuacao,
        password: formData.password,
        callbackUrl
      };
      // Only include invite_code if feature is enabled
      if (FEATURES.REQUIRE_INVITE_CODE) {
        dataToSubmit.invite_code = formData.invite_code;
      }
      // Register the user
      await authService.register(dataToSubmit);
      // Prepare data for CRM registration
      const crmData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: phoneDigits.startsWith('+55') ? phoneDigits : `+55${phoneDigits}`,
        website: website,
        como_voce_ouviu_falar_sobre_a_conversion_: formData.como_voce_ouviu_falar_sobre_a_conversion_ || '',
        a_empresa_em_que_trabalho_e___: formData.a_empresa_em_que_trabalho_e___,
        seu_cargo: formData.seu_cargo,
        numero_de_funcionarios_na_empresa_em_que_trabalho: formData.numero_de_funcionarios_na_empresa_em_que_trabalho,
        area_de_atuacao: formData.area_de_atuacao
      };
      // Only include invite_code if feature is enabled
      if (FEATURES.REQUIRE_INVITE_CODE) {
        crmData.invite_code = formData.invite_code;
      }
      // Send data to CRM (don't await this call to not block the user flow)
      authService.registerCRM(crmData);
      setIsLoading(false);
      setIsSuccess(true);
      addToast('success', 'Conta criada com sucesso! Verifique seu email para ativar.');
    } catch (error) {
      setIsLoading(false);
      // Handle validation errors
      if (error.response?.data?.details) {
        const details = error.response.data.details;
        const errorMessage = details.map(d => d.message).join(', ');
        setError(`Erro de validação: ${errorMessage}`);
      } else {
        setError(error.response?.data?.message || 'Erro ao criar conta. Verifique os dados e tente novamente.');
      }
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto" data-id="element-197">
      <div className="relative w-full max-w-3xl p-6 mx-auto bg-white rounded-lg shadow-xl flex flex-col my-8" data-id="element-198">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700" aria-label="Close" disabled={isLoading} data-id="element-199">
          <X size={20} data-id="element-200" />
        </button>
        {isSuccess ? <div className="py-8 text-center flex-grow flex flex-col items-center justify-center" data-id="element-201">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center" data-id="element-202">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-203">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-204" />
              </svg>
            </div>
            <h2 className="mb-2 text-2xl font-bold text-gray-900" data-id="element-205">
              Conta criada com sucesso!
            </h2>
            <p className="mb-6 text-gray-600" data-id="element-206">
              Enviamos um email de confirmação para{' '}
              <strong data-id="element-207">{formData.email}</strong>. Por favor, verifique sua caixa
              de entrada e clique no link para ativar sua conta.
            </p>
            <button onClick={onClose} className="px-6 py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-208">
              Fechar
            </button>
          </div> : <div className="flex flex-col" data-id="element-209">
            {step === 2 && <div className="flex items-center mb-4" data-id="element-210">
                <button onClick={handlePrevStep} className="mr-2 text-gray-500 hover:text-gray-700" disabled={isLoading} data-id="element-211">
                  <ArrowLeft size={20} data-id="element-212" />
                </button>
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-213">
                  Só faltam estes dados
                </h2>
              </div>}
            {step === 1 && <h2 className="mb-4 text-2xl font-bold text-center text-gray-900" data-id="element-214">
                Precisamos de alguns dados seus
              </h2>}
            {step === 2 && <div className="w-full mb-6 bg-gray-200 rounded-full h-2.5" data-id="element-215">
                <div className="bg-[#1649ff] h-2.5 rounded-full w-3/4" data-id="element-216"></div>
              </div>}
            {pendingAnalysis && <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded-md" data-id="element-217">
                Crie uma conta para continuar com a análise da sua marca.
              </div>}
            {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md" data-id="element-218">
                {error}
              </div>}
            {FEATURES.REQUIRE_INVITE_CODE && isCodeVerified && step === 1 && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md flex items-center" data-id="element-219">
                <CheckCircle size={18} className="mr-2" data-id="element-220" />
                {codeVerificationMessage}
              </div>}
            {step === 1 ? <form data-id="element-221">
                <div className="space-y-4" data-id="element-222">
                  <div className="grid grid-cols-2 gap-4" data-id="element-223">
                    <div data-id="element-224">
                      <label htmlFor="register-firstname" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-225">
                        Nome *
                      </label>
                      <input type="text" id="register-firstname" name="firstname" value={formData.firstname} onChange={handleChange} placeholder="Seu nome" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="given-name" data-id="element-226" />
                    </div>
                    <div data-id="element-227">
                      <label htmlFor="register-lastname" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-228">
                        Sobrenome *
                      </label>
                      <input type="text" id="register-lastname" name="lastname" value={formData.lastname} onChange={handleChange} placeholder="Seu sobrenome" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="family-name" data-id="element-229" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-230">
                    <div data-id="element-231">
                      <label htmlFor="register-email" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-232">
                        E-mail corporativo *
                      </label>
                      <input type="email" id="register-email" name="email" value={formData.email} onChange={handleChange} placeholder="exemplo@empresa.com.br" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="email" data-id="element-233" />
                    </div>
                    <div data-id="element-234">
                      <label htmlFor="register-confirmed-email" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-235">
                        Confirmar e-mail *
                      </label>
                      <input type="email" id="register-confirmed-email" name="confirmed_email" value={formData.confirmed_email} onChange={handleChange} placeholder="exemplo@empresa.com.br" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="email" data-id="element-236" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-237">
                    <div data-id="element-238">
                      <label htmlFor="register-password" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-239">
                        Senha *
                      </label>
                      <input type="password" id="register-password" name="password" value={formData.password} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} minLength={8} autoComplete="new-password" data-id="element-240" />
                      <p className="mt-1 text-xs text-gray-500" data-id="element-241">
                        Mínimo de 8 caracteres, incluindo pelo menos 1 letra, 1
                        número e 1 símbolo
                      </p>
                    </div>
                    <div data-id="element-242">
                      <label htmlFor="register-passwordConfirmation" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-243">
                        Confirmar senha *
                      </label>
                      <input type="password" id="register-passwordConfirmation" name="passwordConfirmation" value={formData.passwordConfirmation} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} minLength={8} autoComplete="new-password" data-id="element-244" />
                    </div>
                  </div>
                  {/* Only show invite code field if feature is enabled */}
                  {FEATURES.REQUIRE_INVITE_CODE && <div className="grid grid-cols-2 gap-4" data-id="element-245">
                      <div data-id="element-246">
                        <label htmlFor="register-invite-code" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-247">
                          Código de convite *
                        </label>
                        <input type="text" id="register-invite-code" name="invite_code" value={formData.invite_code} onChange={handleChange} placeholder="Digite seu código de convite" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading || isVerifyingCode || isCodeVerified} data-id="element-248" />
                      </div>
                      <div className="flex items-end" data-id="element-249">
                        <button type="button" onClick={handleVerifyCode} className="w-full px-3 py-2 bg-white text-[#1649ff] border border-[#1649ff] rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center" disabled={isLoading || isVerifyingCode || isCodeVerified || !formData.invite_code.trim()} data-id="element-250">
                          {isVerifyingCode ? <Loader2 size={16} className="mr-1 animate-spin" data-id="element-251" /> : isCodeVerified ? <CheckCircle size={16} className="mr-1" data-id="element-252" /> : null}
                          {isVerifyingCode ? 'Verificando...' : isCodeVerified ? 'Verificado' : 'Verificar código'}
                        </button>
                      </div>
                    </div>}
                </div>
                <div className="mt-6" data-id="element-253">
                  <button type="button" onClick={handleNextStep} className="w-full py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center" disabled={isLoading || isVerifyingCode} data-id="element-254">
                    {isVerifyingCode ? <>
                        <Loader2 size={18} className="mr-2 animate-spin" data-id="element-255" />
                        Verificando código...
                      </> : 'Continuar'}
                  </button>
                  <div className="mt-4 text-center" data-id="element-256">
                    <p className="text-sm text-gray-600" data-id="element-257">
                      Já tem uma conta?{' '}
                      <button onClick={onLoginClick} className="text-[#1649ff] hover:underline" disabled={isLoading} type="button" data-id="element-258">
                        Entrar
                      </button>
                    </p>
                  </div>
                </div>
              </form> : <form onSubmit={handleSubmit} data-id="element-259">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-260">
                  <div className="md:col-span-1" data-id="element-261">
                    <label htmlFor="register-phone" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-262">
                      Número de telefone *
                    </label>
                    <input type="tel" id="register-phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="(XX) XXXXX-XXXX" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="tel" data-id="element-263" />
                  </div>
                  <div className="md:col-span-1" data-id="element-264">
                    <label htmlFor="register-website" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-265">
                      URL do site *
                    </label>
                    <input type="text" id="register-website" name="website" value={formData.website} onChange={handleChange} placeholder="www.exemplo.com.br" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="url" data-id="element-266" />
                  </div>
                  <div data-id="element-267">
                    <label htmlFor="register-segment" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-268">
                      Segmento *
                    </label>
                    <div className="relative" data-id="element-269">
                      <select id="register-segment" name="a_empresa_em_que_trabalho_e___" value={formData.a_empresa_em_que_trabalho_e___} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} data-id="element-270">
                        <option value="" disabled data-id="element-271">
                          Selecione o segmento
                        </option>
                        <option value="Agência / Consultoria" data-id="element-272">
                          Agência / Consultoria
                        </option>
                        <option value="Agronegócio" data-id="element-273">Agronegócio</option>
                        <option value="Autoconhecimento" data-id="element-274">
                          Autoconhecimento
                        </option>
                        <option value="Automotiva" data-id="element-275">Automotiva</option>
                        <option value="Bens de Consumo / Alimentação" data-id="element-276">
                          Bens de Consumo / Alimentação
                        </option>
                        <option value="E-commerce" data-id="element-277">E-commerce</option>
                        <option value="Educação" data-id="element-278">Educação</option>
                        <option value="Empresa pública / ONGs" data-id="element-279">
                          Empresa pública / ONGs
                        </option>
                        <option value="Energia" data-id="element-280">Energia</option>
                        <option value="Eventos" data-id="element-281">Eventos</option>
                        <option value="Financeiro" data-id="element-282">Financeiro</option>
                        <option value="Imobiliário" data-id="element-283">Imobiliário</option>
                        <option value="Indústria" data-id="element-284">Indústria</option>
                        <option value="Infoprodutos" data-id="element-285">Infoprodutos</option>
                        <option value="Mídia comunicação" data-id="element-286">
                          Mídia comunicação
                        </option>
                        <option value="Mobilidade" data-id="element-287">Mobilidade</option>
                        <option value="Moda / Vestuário" data-id="element-288">
                          Moda / Vestuário
                        </option>
                        <option value="Religião / Espiritualidade" data-id="element-289">
                          Religião / Espiritualidade
                        </option>
                        <option value="Saúde / Beleza / Bem-Estar" data-id="element-290">
                          Saúde / Beleza / Bem-Estar
                        </option>
                        <option value="Serviços gerais" data-id="element-291">Serviços gerais</option>
                        <option value="Tecnologia / SaaS" data-id="element-292">
                          Tecnologia / SaaS
                        </option>
                        <option value="Telecomunicações" data-id="element-293">
                          Telecomunicações
                        </option>
                        <option value="Transporte / Logística" data-id="element-294">
                          Transporte / Logística
                        </option>
                        <option value="Turismo / Lazer" data-id="element-295">Turismo / Lazer</option>
                        <option value="Varejo" data-id="element-296">Varejo</option>
                        <option value="Outro" data-id="element-297">Outro</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-298" />
                    </div>
                  </div>
                  <div data-id="element-299">
                    <label htmlFor="register-cargo" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-300">
                      Seu cargo *
                    </label>
                    <div className="relative" data-id="element-301">
                      <select id="register-cargo" name="seu_cargo" value={formData.seu_cargo} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} autoComplete="organization-title" data-id="element-302">
                        <option value="" disabled data-id="element-303">
                          Selecione seu cargo
                        </option>
                        <option value="CEO" data-id="element-304">CEO</option>
                        <option value="Fundador" data-id="element-305">Fundador</option>
                        <option value="Presidente" data-id="element-306">Presidente</option>
                        <option value="Diretor" data-id="element-307">Diretor</option>
                        <option value="Gerente" data-id="element-308">Gerente</option>
                        <option value="Coordenador" data-id="element-309">Coordenador</option>
                        <option value="Analista" data-id="element-310">Analista</option>
                        <option value="Consultor" data-id="element-311">Consultor</option>
                        <option value="Autônomo" data-id="element-312">Autônomo</option>
                        <option value="Supervisor" data-id="element-313">Supervisor</option>
                        <option value="Assessor" data-id="element-314">Assessor</option>
                        <option value="Assistente" data-id="element-315">Assistente</option>
                        <option value="Estagiário" data-id="element-316">Estagiário</option>
                        <option value="Estudante" data-id="element-317">Estudante</option>
                        <option value="Professor" data-id="element-318">Professor</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-319" />
                    </div>
                  </div>
                  <div data-id="element-320">
                    <label htmlFor="register-funcionarios" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-321">
                      Número de funcionários *
                    </label>
                    <div className="relative" data-id="element-322">
                      <select id="register-funcionarios" name="numero_de_funcionarios_na_empresa_em_que_trabalho" value={formData.numero_de_funcionarios_na_empresa_em_que_trabalho} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} data-id="element-323">
                        <option value="" disabled data-id="element-324">
                          Selecione a quantidade
                        </option>
                        <option value="1 - 10" data-id="element-325">1 - 10</option>
                        <option value="11 - 20" data-id="element-326">11 - 20</option>
                        <option value="21 - 50" data-id="element-327">21 - 50</option>
                        <option value="51 - 100" data-id="element-328">51 - 100</option>
                        <option value="101 - 200" data-id="element-329">101 - 200</option>
                        <option value="201 - 500" data-id="element-330">201 - 500</option>
                        <option value="501 - 1.000" data-id="element-331">501 - 1.000</option>
                        <option value="1.001 - 5.000" data-id="element-332">1.001 - 5.000</option>
                        <option value="Acima de 5.000" data-id="element-333">Acima de 5.000</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-334" />
                    </div>
                  </div>
                  <div data-id="element-335">
                    <label htmlFor="register-area" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-336">
                      Área de atuação *
                    </label>
                    <div className="relative" data-id="element-337">
                      <select id="register-area" name="area_de_atuacao" value={formData.area_de_atuacao} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] appearance-none text-gray-900" required disabled={isLoading} data-id="element-338">
                        <option value="" disabled data-id="element-339">
                          Selecione a área
                        </option>
                        <option value="Administrativo" data-id="element-340">Administrativo</option>
                        <option value="Comercial" data-id="element-341">Comercial</option>
                        <option value="Contábil" data-id="element-342">Contábil</option>
                        <option value="Customer Success" data-id="element-343">
                          Customer Success
                        </option>
                        <option value="E-commerce" data-id="element-344">E-commerce</option>
                        <option value="Financeiro" data-id="element-345">Financeiro</option>
                        <option value="Marketing" data-id="element-346">Marketing</option>
                        <option value="Operação" data-id="element-347">Operação</option>
                        <option value="RH" data-id="element-348">RH</option>
                        <option value="SEO" data-id="element-349">SEO</option>
                        <option value="Tecnologia" data-id="element-350">Tecnologia</option>
                        <option value="Outra área" data-id="element-351">Outra área</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-352" />
                    </div>
                  </div>
                  <div className="md:col-span-2" data-id="element-353">
                    <label htmlFor="register-como-conheceu" className="block mb-2 text-sm font-medium text-gray-700" data-id="element-354">
                      Como você ouviu falar sobre a Conversion?
                    </label>
                    <input type="text" id="register-como-conheceu" name="como_voce_ouviu_falar_sobre_a_conversion_" value={formData.como_voce_ouviu_falar_sobre_a_conversion_} onChange={handleChange} placeholder="Google, indicação, redes sociais, etc." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" disabled={isLoading} data-id="element-355" />
                  </div>
                </div>
                <div className="mt-6 flex space-x-4" data-id="element-356">
                  <button type="button" onClick={handlePrevStep} className="w-1/3 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2" disabled={isLoading} data-id="element-357">
                    Voltar
                  </button>
                  <button type="submit" className="w-2/3 py-2 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center" disabled={isLoading} data-id="element-358">
                    {isLoading ? <>
                        <Loader2 size={18} className="mr-2 animate-spin" data-id="element-359" />
                        Processando...
                      </> : 'Cadastre-se'}
                  </button>
                </div>
              </form>}
          </div>}
      </div>
    </div>;
};
export default RegisterModal;